import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const LINE_WARNINGS = {
  MISSING_PRICE: "חסר מחיר ספק — עמודת Price תישאר ריקה",
  MISSING_SIZE: "לא נמצאה מידה",
  MISSING_SUPPLIER: "חסר ספק — עמודת Source תישאר ריקה",
  MISSING_EAN: "חסר EAN",
};

const SETTINGS_ERROR_CODES = {
  NOT_CONFIGURED: true,
  DECRYPT_FAILED: true,
  PERMISSION_DENIED: true,
  SHEETS_API_DISABLED: true,
  AUTH_FAILED: true,
  SHEET_URL_MISSING: true,
};

const styles = {
  ltr: {
    direction: "ltr",
    textAlign: "left",
  },
  warnRow: {
    backgroundColor: "#fff7ed",
  },
  amberBox: {
    background: "#fffbeb",
    border: "1px solid #f59e0b",
    borderRadius: 6,
    padding: "10px 12px",
    margin: "12px 0",
  },
};

function formatDmY(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${date.getFullYear()}`;
}

function skippedText(item) {
  if (item.reason === "ALREADY_SYNCED") {
    const date = formatDmY(item.syncedAt);
    return date ? `כבר סונכרנה לגיליון זה ב-${date}` : "כבר סונכרנה לגיליון זה";
  }
  if (item.reason === "FOUND_IN_SHEET") return `ההזמנה כבר מופיעה בגיליון (PO#${item.orderId})`;
  if (item.reason === "CANCELLED_OR_FULFILLED") return "בוטלה / הושלמה";
  if (item.reason === "NOT_FOUND") return "לא נמצאה";
  return item.reason || "";
}

function warningText(code) {
  return LINE_WARNINGS[code] || code;
}

class PreorderSheetSyncDialog extends Component {
  constructor(props) {
    super(props);
    this.previewRequest = 0;
    this.state = {
      loadingPreview: false,
      syncing: false,
      preview: null,
      result: null,
      error: null,
      errorCode: null,
      force: false,
      showFixes: false,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.props.open && prevProps.open) {
      this.previewRequest += 1;
      this.setState({
        force: false,
        result: null,
        error: null,
        errorCode: null,
        preview: null,
        showFixes: false,
        syncing: false,
        loadingPreview: false,
      });
      return;
    }
    if (this.props.open && !prevProps.open) {
      this.loadPreview();
      return;
    }
    if (this.props.open && prevProps.open && prevState.force !== this.state.force) {
      this.loadPreview();
    }
  }

  loadPreview = async () => {
    this.previewRequest += 1;
    const requestId = this.previewRequest;
    this.setState({ loadingPreview: true, error: null, errorCode: null, preview: null, result: null });
    try {
      const res = await fetch(`${basePath}/api/v1/on-demand-orders/sheet-sync/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify({
          orderIds: this.props.orderIds || [],
          force: this.state.force,
        }),
      });
      const data = await res.json();
      if (requestId !== this.previewRequest) return;
      if (!data.ok) {
        this.setState({
          loadingPreview: false,
          error: data.error || "התצוגה המקדימה נכשלה",
          errorCode: data.code || null,
        });
        return;
      }
      this.setState({ loadingPreview: false, preview: data });
    } catch (err) {
      if (requestId !== this.previewRequest) return;
      this.setState({ loadingPreview: false, error: err.message });
    }
  };

  handleClose = () => {
    if (this.state.syncing) return;
    this.props.onClose();
  };

  handleSync = async () => {
    this.setState({ syncing: true, error: null, errorCode: null });
    try {
      const res = await fetch(`${basePath}/api/v1/on-demand-orders/sheet-sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify({
          orderIds: this.props.orderIds || [],
          force: this.state.force,
        }),
      });
      const data = await res.json();
      if (!data.ok) {
        this.setState({
          syncing: false,
          error: data.error || "הסנכרון נכשל",
          errorCode: data.code || null,
        });
        return;
      }
      this.setState({ syncing: false, result: data });
    } catch (err) {
      this.setState({ syncing: false, error: err.message });
    }
  };

  renderError() {
    const { error, errorCode } = this.state;
    if (!error) return null;
    return (
      <Typography style={{ color: "#c62828", marginTop: 12 }}>
        {error}
        {SETTINGS_ERROR_CODES[errorCode] && (
          <>
            {" "}
            <Link to="/admin/google-sheets-sync">חיבור Google Sheets</Link>
          </>
        )}
      </Typography>
    );
  }

  renderResult(classes) {
    const { result } = this.state;
    const verification = result.verification || {};
    const totalsWarning = (result.warnings || []).find((warning) => warning.code === "TOTALS_NOT_UPDATED");
    return (
      <>
        <DialogContent>
          {result.nothing ? (
            <Typography>אין שורות להוספה</Typography>
          ) : (
            <Typography>
              נוספו {result.inserted && result.inserted.count} שורות (שורות {result.inserted && result.inserted.fromRow}–{result.inserted && result.inserted.toRow})
              {result.sheetLink && (
                <>
                  {" "}
                  <a href={result.sheetLink} target="_blank" rel="noopener noreferrer">פתח בגיליון</a>
                </>
              )}
            </Typography>
          )}
          {verification.ok && (
            <Typography style={{ color: "#16a34a", marginTop: 8 }}>
              סך היחידות עודכן: {verification.unitsBefore} → {verification.unitsAfter}
            </Typography>
          )}
          {totalsWarning && (
            <div className={classes.amberBox}>{totalsWarning.message}</div>
          )}
          {(result.warnings || []).filter((warning) => warning.code !== "TOTALS_NOT_UPDATED").map((warning) => (
            <Typography key={`${warning.code}-${warning.orderId || ""}`} style={{ marginTop: 6 }}>
              {warning.orderId ? `#${warning.orderId}: ` : ""}{warning.message || warning.code}
            </Typography>
          ))}
          {(result.skipped || []).length > 0 && (
            <div style={{ marginTop: 12 }}>
              {result.skipped.map((item) => (
                <Typography key={`${item.orderId}-${item.reason}`} variant="body2">
                  #{item.orderId} — {skippedText(item)}
                </Typography>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={() => this.props.onSynced(result)}>
            סגור
          </Button>
        </DialogActions>
      </>
    );
  }

  renderPreview(classes) {
    const { loadingPreview, syncing, preview, force, showFixes } = this.state;
    const lines = (preview && preview.lines) || [];
    const layout = (preview && preview.layout) || {};
    const target = (preview && preview.target) || {};
    const fixes = (preview && preview.formulaFixes) || [];
    return (
      <>
        <DialogContent>
          {target.spreadsheetTitle && (
            <Typography>
              {target.spreadsheetTitle} › {target.sheetTitle}
              {target.sheetUrl && (
                <>
                  {" "}
                  <a href={target.sheetUrl} target="_blank" rel="noopener noreferrer">פתח גיליון</a>
                </>
              )}
            </Typography>
          )}
          {loadingPreview && <div style={{ textAlign: "center", padding: 24 }}><CircularProgress /></div>}
          {preview && (
            <Typography style={{ marginTop: 8 }}>
              השורות יתווספו אחרי שורה {layout.lastProductRow}
              {layout.totalsStartRow ? ` (לפני בלוק הסיכומים בשורה ${layout.totalsStartRow})` : ""}
              {layout.needsSeparator ? " + שורה ריקה מפרידה" : ""}
            </Typography>
          )}
          {fixes.length > 0 && (
            <div className={classes.amberBox}>
              {fixes.length} נוסחאות סיכום יורחבו כך שיכללו את השורות החדשות
              <div>
                <Button size="small" onClick={() => this.setState({ showFixes: !showFixes })}>
                  {showFixes ? "הסתר" : "הצג"}
                </Button>
              </div>
              {showFixes && fixes.map((fix) => (
                <div key={fix.cellA1After} style={{ direction: "ltr", textAlign: "left" }}>
                  <bdi dir="ltr">{fix.before} → {fix.after}</bdi>
                </div>
              ))}
            </div>
          )}
          {preview && (preview.warnings || []).map((warning) => (
            <Typography key={`${warning.code}-${warning.orderId || "layout"}`} style={{ marginTop: 6 }}>
              {warning.orderId ? `#${warning.orderId}: ` : ""}{warning.message}
            </Typography>
          ))}
          {lines.length > 0 && (
            <Table size="small" style={{ marginTop: 12 }}>
              <TableHead>
                <TableRow>
                  {["הזמנה", "EAN", "Brand", "Pattern", "Size", "Quantity", "Price", "Source", "הערה"].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, index) => {
                  const warnings = line.warnings || [];
                  const note = warnings.length ? (
                    <Tooltip title={warnings.map(warningText).join(" · ")}>
                      <span>{line.note}</span>
                    </Tooltip>
                  ) : line.note;
                  return (
                    <TableRow key={`${line.orderId}-${line.ean}-${line.position || "single"}-${index}`} className={warnings.length ? classes.warnRow : undefined}>
                      <TableCell>{line.orderId}</TableCell>
                      <TableCell className={classes.ltr}><bdi dir="ltr">{line.ean}</bdi></TableCell>
                      <TableCell className={classes.ltr}><bdi dir="ltr">{line.brand}</bdi></TableCell>
                      <TableCell className={classes.ltr}><bdi dir="ltr">{line.pattern}</bdi></TableCell>
                      <TableCell className={classes.ltr}><bdi dir="ltr">{line.size}</bdi></TableCell>
                      <TableCell className={classes.ltr}><bdi dir="ltr">{line.quantity}</bdi></TableCell>
                      <TableCell className={classes.ltr}><bdi dir="ltr">{line.priceEUR == null ? "" : line.priceEUR}</bdi></TableCell>
                      <TableCell className={classes.ltr}><bdi dir="ltr">{line.source}</bdi></TableCell>
                      <TableCell>{note}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {preview && (preview.skipped || []).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Typography variant="subtitle2">לא יסונכרנו</Typography>
              {preview.skipped.map((item) => (
                <Typography key={`${item.orderId}-${item.reason}`} variant="body2">
                  #{item.orderId} — {skippedText(item)}
                </Typography>
              ))}
            </div>
          )}
          {this.renderError()}
          <FormControlLabel
            style={{ marginTop: 12 }}
            control={(
              <Checkbox
                checked={force}
                onChange={(event) => this.setState({ force: event.target.checked })}
                color="primary"
              />
            )}
            label="סנכרן שוב גם הזמנות שכבר סונכרנו"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleClose} disabled={syncing}>ביטול</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={this.handleSync}
            disabled={syncing || loadingPreview || lines.length === 0}
          >
            {syncing ? <CircularProgress size={20} /> : `הוסף ${lines.length} שורות לגיליון`}
          </Button>
        </DialogActions>
      </>
    );
  }

  render() {
    const { open, orderIds, classes } = this.props;
    const { result } = this.state;
    const count = (orderIds || []).length;
    return (
      <Dialog
        open={open}
        onClose={this.handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ style: { direction: "rtl" } }}
      >
        <DialogTitle>
          סנכרון {count} הזמנות לגיליון ההזמנה
        </DialogTitle>
        {result ? this.renderResult(classes) : this.renderPreview(classes)}
      </Dialog>
    );
  }
}

PreorderSheetSyncDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool,
  orderIds: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSynced: PropTypes.func.isRequired,
};

PreorderSheetSyncDialog.defaultProps = {
  open: false,
  orderIds: [],
};

export default withStyles(styles)(PreorderSheetSyncDialog);
